import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';

import {
  bibShape,
  availabilityShape,
  catalogBibShape,
  libraryShape,
  holdingShape,
  subscriptionShape,
  userIdentityShape
} from '@bibliocommons/bc-prop-types';
import Svg from '@bibliocommons/svg';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { TitleTransactionButtonContainer } from 'app/components/bibs/shared/TitleTransactionButton';
import { ShelfActionsButtonContainer } from 'app/components/bibs/shared/ShelfActionsButton';
import AddToListDropdown from 'app/components/bibs/shared/AddToListDropdown';
import CirculationInfo from './CirculationInfo';
import DigitalContentInfo from './DigitalContentInfo';
import BranchAvailabilityInfo from './BranchAvailabilityInfo';
import ElectronicResourceInfo from './ElectronicResourceInfo';
import AvailabilityErrorInfo from './AvailabilityErrorInfo';

import './TransactPanel.scss';

function renderPlaceholder() {
  return (
    <div>
      <div className="panel-section">
        <Svg className="panel-placeholder" width="264" height="54" viewBox="0 0 264 54" preserveAspectRatio="none">
          <rect width="187" height="20" y="5" />
          <rect width="259" height="14" y="36" />
        </Svg>
      </div>
      <div className="panel-section">
        <Svg className="panel-placeholder" width="264" height="152" viewBox="0 0 264 152" preserveAspectRatio="none">
          <rect width="120" height="14" y="6" />
          <rect width="264" height="40" y="44" rx="4" />
          <path d="M4.48301887,103 L259.516981,103 C261.992884,103 264,105.014719 264,107.5 L264,139.5 C264,141.985281 261.992884,144 259.516981,144 L4.48301887,144 C2.00711591,144 0,141.985281 0,139.5 L0,107.5 C0,105.014719 2.00711591,103 4.48301887,103 Z M4.48301887,104 C2.55731657,104 0.996226415,105.567003 0.996226415,107.5 L0.996226415,139.5 C0.996226415,141.432997 2.55731657,143 4.48301887,143 L259.516981,143 C261.442683,143 263.003774,141.432997 263.003774,139.5 L263.003774,107.5 C263.003774,105.567003 261.442683,104 259.516981,104 L4.48301887,104 Z" />
        </Svg>
      </div>
    </div>
  );
}

export default function TransactPanel({
  bib,
  bibItems,
  isLoaded,
  realtimeAvailability,
  preferredBranches,
  digitalFormats,
  availabilityError,
  catalogBib,
  currentLibrary,
  holdings,
  subscriptions,
  currentUser,
  bibsActions
}) {
  function renderAddToListDropdown() {
    return currentUser?.get('staff') ? (
      <AddToListDropdown block catalogBib={catalogBib} overlayKey="add-to-list-overlay-transact-panel" />
    ) : null;
  }

  function renderTransactionButton() {
    return bib.getIn(['policy', 'holdable']) ? (
      <TitleTransactionButtonContainer block metadataId={bib.get('id')} />
    ) : (
      <div className="non-holdable-title-info">
        <FormattedMessage id="title_not_holdable" />
      </div>
    );
  }

  function renderShelfActionsButton() {
    const metadataId = bib.get('id');
    return (
      <ShelfActionsButtonContainer
        block
        metadataId={metadataId}
        handleDeleteSuccess={() => bibsActions.fetchUGC(metadataId)}
      />
    );
  }

  function renderContent() {
    if (realtimeAvailability) {
      const bibType = realtimeAvailability.get('bibType');
      const totalCopies = realtimeAvailability.get('totalCopies');
      const subscriptionsCount = subscriptions?.size || 0;
      const superFormats = bib.getIn(['briefInfo', 'superFormats']);

      const hideCirculationForElectronicFormats =
        superFormats.includes('ELECTRONIC_FORMATS') && currentLibrary.get('hideCirculationForElectronicFormats');

      if (
        bibType === 'DIGITAL_RESOURCE' &&
        ((totalCopies === 0 && subscriptionsCount === 0) || hideCirculationForElectronicFormats)
      ) {
        return (
          <div className="panel-section electronic-section">
            <DigitalContentInfo bib={bib} digitalFormats={digitalFormats} />
            <ElectronicResourceInfo realtimeAvailability={realtimeAvailability} holdings={holdings} />
            {renderShelfActionsButton()}
            {renderAddToListDropdown()}
          </div>
        );
      }

      if (totalCopies > 0 || subscriptionsCount > 0) {
        return (
          <div>
            <div className="panel-section circulation-section">
              <CirculationInfo
                availabilityError={availabilityError}
                realtimeAvailability={realtimeAvailability}
                currentLibrary={currentLibrary}
              />
            </div>
            <div className="panel-section availability-section">
              <BranchAvailabilityInfo
                bib={bib}
                bibItems={bibItems}
                preferredBranches={preferredBranches}
                realtimeAvailability={realtimeAvailability}
                subscriptions={subscriptions}
              />
              <DigitalContentInfo bib={bib} digitalFormats={digitalFormats} />
              <ElectronicResourceInfo realtimeAvailability={realtimeAvailability} holdings={holdings} />
              {renderTransactionButton()}
              {renderShelfActionsButton()}
              {renderAddToListDropdown()}
            </div>
          </div>
        );
      }
    }

    return (
      <div className="panel-section error-section">
        <AvailabilityErrorInfo
          availabilityError={availabilityError}
          realtimeAvailability={realtimeAvailability}
          currentLibrary={currentLibrary}
          catalogBib={catalogBib}
        />
        {renderShelfActionsButton()}
        {renderAddToListDropdown()}
      </div>
    );
  }

  return (
    <div className="cp-transact-panel" data-key="transact-panel">
      <ScreenReaderMessage>
        <h2>
          <FormattedMessage id="title_availability" />
        </h2>
      </ScreenReaderMessage>
      {isLoaded && bib ? renderContent() : renderPlaceholder()}
    </div>
  );
}

TransactPanel.propTypes = {
  bib: bibShape.isRequired,
  bibItems: ImmutablePropTypes.list.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  realtimeAvailability: availabilityShape,
  preferredBranches: ImmutablePropTypes.list.isRequired,
  digitalFormats: ImmutablePropTypes.list,
  availabilityError: PropTypes.string,
  catalogBib: catalogBibShape.isRequired,
  currentLibrary: libraryShape.isRequired,
  holdings: ImmutablePropTypes.listOf(holdingShape).isRequired,
  subscriptions: ImmutablePropTypes.listOf(subscriptionShape).isRequired,
  bibsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  currentUser: userIdentityShape
};
